/**
 * Iter269.20y — Sticky table-of-contents sidebar.
 *
 * Auto-generates a navigable TOC from any `<h2>` / `<h3>` headings
 * inside a container ref or CSS selector. Designed for long-form
 * pages (Methodology, Audit Suite, Help Center articles) where
 * investors / auditors want to jump straight to a section.
 *
 * Behaviour:
 *   • Renders as a fixed right-side sidebar on `lg+` screens.
 *   • Hidden on smaller screens (mobile/tablet users get the
 *     reading-progress bar instead).
 *   • Headings without an `id` get a slugified id assigned in-place
 *     so deep links survive route changes.
 *   • Uses IntersectionObserver to track the section closest to the
 *     viewport top and highlights it in the TOC.
 *   • Click on a TOC entry → smooth scroll to the heading with a
 *     small top offset so the sticky nav doesn't cover the title.
 *   • Re-scans the DOM after a short settle delay so lazy-loaded
 *     sections still appear in the TOC.
 *
 * Usage:
 *   <StickyToc containerSelector=".methodology-content" />
 *
 * If `containerSelector` is omitted, falls back to `main` then
 * `document.body`.
 */
import React, { useEffect, useRef, useState, useCallback } from "react";

const NAV_OFFSET_PX = 88;        // sticky nav (~64px) + breathing room
const RESCAN_DELAY_MS = 800;     // re-query after late-mount content
const HEADING_SELECTOR = "h2, h3";

function slugify(text) {
  return String(text || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")    // strip diacritics
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80) || "section";
}

export default function StickyToc({
  containerSelector,
  title = "On this page",
  minHeadings = 2,
}) {
  const [items, setItems]     = useState([]);
  const [activeId, setActive] = useState(null);
  const observerRef = useRef(null);

  // ── DOM scan + id assignment ─────────────────────────────────────
  const scan = useCallback(() => {
    let root = null;
    if (containerSelector) root = document.querySelector(containerSelector);
    if (!root) root = document.querySelector("main");
    if (!root) root = document.body;
    if (!root) return [];

    const heads = Array.from(root.querySelectorAll(HEADING_SELECTOR))
      .filter((h) => (h.textContent || "").trim().length > 1)
      .filter((h) => !h.closest("[data-toc-ignore]"));

    const seen = new Map();
    const next = heads.map((h) => {
      let id = h.id;
      if (!id) {
        const base = slugify(h.textContent || "section");
        const n = (seen.get(base) || 0) + 1;
        seen.set(base, n);
        id = n === 1 ? base : `${base}-${n}`;
        h.id = id;
        // Soft "anchor" offset so the sticky nav doesn't cover the heading
        // when navigated to. Inline so we don't need a global stylesheet.
        h.style.scrollMarginTop = h.style.scrollMarginTop || `${NAV_OFFSET_PX}px`;
      }
      return {
        id,
        text: (h.textContent || "").trim(),
        level: h.tagName === "H3" ? 3 : 2,
        el: h,
      };
    });
    return next;
  }, [containerSelector]);

  // ── Initial scan + IntersectionObserver setup ──────────────────
  useEffect(() => {
    const setup = () => {
      const found = scan();
      setItems(found);

      // Tear down any previous observer
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      if (found.length === 0) return;

      const visible = new Map();
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) visible.set(e.target.id, e.boundingClientRect.top);
            else visible.delete(e.target.id);
          });
          if (visible.size === 0) return;
          // Pick the section closest to (but below) the nav offset.
          const sorted = [...visible.entries()].sort((a, b) => a[1] - b[1]);
          setActive(sorted[0][0]);
        },
        {
          rootMargin: `-${NAV_OFFSET_PX + 8}px 0px -65% 0px`,
          threshold:  [0, 1],
        },
      );
      found.forEach((it) => io.observe(it.el));
      observerRef.current = io;
    };

    setup();
    const t = setTimeout(setup, RESCAN_DELAY_MS);   // rescan after content settles

    return () => {
      clearTimeout(t);
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [scan]);

  const handleClick = useCallback((e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - NAV_OFFSET_PX;
    try {
      window.scrollTo({ top: y, behavior: "smooth" });
    } catch {
      window.scrollTo(0, y);
    }
    // Update the URL with the hash so the link is shareable.
    if (window.history && window.history.replaceState) {
      window.history.replaceState(null, "", `#${id}`);
    }
    setActive(id);
  }, []);

  if (items.length < minHeadings) return null;

  return (
    <nav
      data-testid="sticky-toc"
      aria-label="Table of contents"
      className="hidden lg:block"
      style={{
        position: "fixed",
        top:      NAV_OFFSET_PX + 8,
        right:    18,
        width:    248,
        // Iter273.2 — Beta-tester fix: TOC was overlapping Qelli's
        // avatar + speech bubble in the bottom-right (Qelli sits
        // around bottom 56-280px on default theme). Constrain the
        // TOC's bottom so the avatar/bubble has clear breathing room.
        // The bubble peaks around 280px above the viewport bottom;
        // padding to 240px keeps a 40px safety gap.
        maxHeight: `calc(100vh - ${NAV_OFFSET_PX + 24}px - 240px)`,
        overflowY: "auto",
        padding:   "16px 14px",
        borderRadius: 10,
        background: "rgba(11,19,36,0.55)",
        border:     "1px solid rgba(255,255,255,0.06)",
        backdropFilter: "blur(10px)",
        zIndex:     45,            // below nav (50), below progress bar (60)
        boxShadow:  "0 4px 24px rgba(0,0,0,0.20)",
      }}
    >
      <div
        className="qt-mono"
        style={{
          fontSize: 10,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: "var(--qt-text-3, #94A3B8)",
          marginBottom: 12,
        }}
      >
        {title}
      </div>
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {items.map((it) => {
          const active = activeId === it.id;
          return (
            <li key={it.id} style={{ margin: "2px 0" }}>
              <a
                href={`#${it.id}`}
                onClick={(e) => handleClick(e, it.id)}
                data-testid={`toc-link-${it.id}`}
                data-active={active ? "true" : "false"}
                style={{
                  display:        "block",
                  paddingTop:     4,
                  paddingBottom:  4,
                  paddingInlineStart: it.level === 3 ? 22 : 10,
                  fontSize:       13,
                  lineHeight:     1.4,
                  color:          active ? "var(--qt-cyan, #00E5FF)" : "#CBD5E1",
                  borderLeft:     active
                                    ? "2px solid var(--qt-cyan, #00E5FF)"
                                    : "2px solid transparent",
                  textDecoration: "none",
                  transition:     "color 140ms ease, border-color 140ms ease",
                  fontWeight:     active ? 600 : 400,
                  whiteSpace:     "nowrap",
                  overflow:       "hidden",
                  textOverflow:   "ellipsis",
                }}
                title={it.text}
              >
                {it.text}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
